import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { DateRangeType } from "react-tailwindcss-datepicker";

import { CameraIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { StarIcon } from '@heroicons/react/24/solid';
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { ILead } from "../../models/Lead";
import { PageMode } from "../../models/PagedTable";
import openInNewTab from "../../utils/openInNewTab";
import toIsoDate from "../../utils/toIsoDate";
import PagedTable from "../shared/Tables/PagedTable";
import EnginesWorldWideOffers from "../vehiclesAwaitingArrival/EnginesWorldWideOffers";
import AwaitingInventoryEbayPrice from "../awaitingInventory/AwaitingInventoryEbayPrice";
import AwaitingInventoryPrice from "../awaitingInventory/AwaitingInventoryPrice";
import AwaitingInventoryVfePrice from "../awaitingInventory/AwaitingInventoryVfePrice";
import CustomDatePicker from "../awaitingInventory/CustomDatePicker";
import ExportOffersTableRow from "./ExportOffersTableRow";
import { useAuth } from "../../contexts/AuthContext";
import PartVehicleImages from "../leads/LeadParts/LeadImagesCarousel";
import ClickToCopy from "../shared/ClickToCopy";
import AssignExporterModal, { AssignExporterForm } from "./AssignExporterModal";
import ExporterSelect from "./ExporterSelect";
import { useForm } from "react-hook-form";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-2 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<ILead>();

export default function ExportsTable() {
  const { register, setValue, watch } = useForm<any>();
  const { claims } = useAuth();
  const [assignExporter, setAssignExporter] = useState<AssignExporterForm | null>(null);

  let cols: ColumnDef<ILead, any>[] = useMemo(
    () => [
    {
      id: 'select',
      header: ({ table }: any) => (
        // <IndeterminateCheckbox
        //   {...{
        //     checked: table.getIsAllRowsSelected(),
        //     indeterminate: table.getIsSomeRowsSelected(),
        //     onChange: table.getToggleAllRowsSelectedHandler(),
        //   }}
        // />
        <span></span>
      ),
      cell: ({ row }: any) => (
        <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
          {/* <IndeterminateCheckbox
            {...{
              id: row.original.id,
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          /> */}
          <button
            onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
            style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
            className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
          >
            {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    colHelper.display({
      id: "image",
      cell: (info) => (
        <span className="flex justify-center relative" >
          {info.row.original.vehicle?.vehicleImageUrl ? (
            <>
              <img
                className="w-32 h-20 object-contain"
                src={`${info.row.original.vehicle != null ? info.row.original.vehicle.vehicleImageUrl : null}`}
              />
              {info.row.original.vehicle?.vehiclePremium && (
                <StarIcon className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
              )}

            </>
          ) : (
            <CameraIcon className="w-14 text-gray-600" />
          )}
        </span>
      ),
    }),
    colHelper.accessor("vehicle.combinedMake", {
      id: "combinedMake",
      header: "Make & Model",
      enableSorting: false,
      cell: (info) => <div>
        <p>{info.row.original.vehicle?.combinedMake}</p>
        <p>{info.row.original.vehicle?.combinedModel}</p>
      </div>,
    }),
    colHelper.accessor("vehicle.vrm", {
      id: "vrm",
      header: "VRM/VIN",
      enableSorting: false,
      cell: (info) => <div>
        {/* <p>{info.row.original.vehicle?.vrm}</p> */}
        <ClickToCopy text={info.row.original.vehicle?.vrm} />
        {/* <p>{info.row.original.vehicle?.combinedVIN}</p> */}
        <ClickToCopy text={info.row.original.vehicle?.combinedVIN} />
      </div>,
    }),
    colHelper.accessor("vehicle.engineModelCode", {
      id: "engineModelCode",
      header: "Engine",
      enableSorting: false,
      cell: (info) => <div>
        <p>{info.row.original.vehicle?.engineModelCode}</p>
        <p>{info.row.original.vehicle?.combinedFuelType}</p>
        {info.row.original.vehicle?.combinedForwardGears && (<p>{`${info.row.original.vehicle?.combinedForwardGears} Speed ${info.row.original.vehicle?.combinedTransmission}`}</p>)}
        {info.row.original.vehicle?.maximumPowerBHP && (<p>{info.row.original.vehicle?.maximumPowerBHP} BHP</p>)}
      </div>,
    }),
    colHelper.accessor("vehicle.bodyStyleDescription", {
      id: "bodyStyleDescription",
      header: "Mileage & Body Type",
      enableSorting: false,
      cell: (info) => <div>
        <p>{info.row.original.vehicle?.odometerInMiles}</p>
        <p>{info.row.original.vehicle?.bodyStyleDescription}</p>
      </div>,
    }),
    colHelper.accessor("vehicle.exportOffers", {
      id: "exportOffers",
      header: "Export Offers",
      enableSorting: false,
      cell: (info) => <ExportOffersTableRow vehicle={info.row.original?.vehicle!} />,
    }),
  ], []);

  const staffCols: ColumnDef<ILead, any>[] = useMemo(
    () => [
    // colHelper.accessor("vehicle.valuationTradePoor", {
    //   id: "valuationTradePoor",
    //   header: "Salvage",
    //   enableSorting: false,
    //   cell: (info) => <span>{PriceFormat(info.row.original.vehicle?.valuationTradePoor)}</span>,
    // }),
    colHelper.accessor("vehicle.ebayEnginePrice", {
      id: "ebayEnginePrice",
      header: "Ebay Engine £",
      cell: (info) => <AwaitingInventoryEbayPrice lead={info.row.original} />,
    }),
    colHelper.accessor("vehicle.enginesWorldwideEngineOffer", {
      id: "enginesWorldwideEngineOffer",
      header: "EWW Export Offers £",
      cell: (info) => <div>
        <p>Engine Offer</p>
        <AwaitingInventoryPrice vehicle={info.row.original.vehicle!} setAssignExporter={setAssignExporter} />
        <p className="mt-2 ">VFE Offer</p>
        <AwaitingInventoryVfePrice vehicle={info.row.original.vehicle!} setAssignExporter={setAssignExporter} />
      </div>
    }),
    colHelper.accessor("vehicle.enginesWorldwide", {
      id: "enginesWorldwide",
      header: "EWW Export Status",
      sortingFn: "auto",
      cell: (info) => <EnginesWorldWideOffers offers={info.getValue()} />
    }),
  ], [setAssignExporter]);

  const finalCols = useMemo(() => {
    if (claims?.isAdmin || !claims?.isExporter) {
      return [...cols, ...staffCols];
    }
    return cols;
  }, [cols, staffCols, claims?.isAdmin, claims?.isExporter]);

  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});

  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });

  const handleDatesChange = (newDates: any) => {
    setDates(newDates);
  };

  const userId = watch("userId");

  useEffect(() => {
    setAdditionalBody({
      startDate: dates.startDate ? toIsoDate(dates.startDate) : undefined,
      endDate: dates.endDate ? toIsoDate(dates.endDate) : undefined,
      includeUnscheduled: false,
      exporterId: userId,
    });
  }, [dates, userId]);

  useEffect(() => {
    setValue("userId", null);
  const userId = watch("userId");
  }, [setValue]);


  return (
    <>
      <AssignExporterModal initialValues={assignExporter} setInitialValues={setAssignExporter} />
      {/* ---------- Search ---------- */}
      <div className="mt-4 flex justify-between">
        <div className="flex gap-x-5">
          {/* <select aria-label="Contact type filter"
            onChange={(e) => setCurrentFilter(e.target.value)}
            className="sm:text-sm">
            {filters.map((filter) => (
              <option
                className="sm:text-sm"
                key={filter}>
                {String(filter)}
              </option>
            ))}
          </select> */}

          <div className="mt-2">
            <CustomDatePicker dates={dates} handleDatesChange={handleDatesChange} />
          </div>

          <div className="w-60" >
            <ExporterSelect register={register} setValue={setValue} watch={watch} hideLabel />
          </div>

        </div>
      </div>

      <PagedTable
        key="exports-table"
        url="/lead/partial"

        columns={finalCols}
        pageMode={PageMode.Exports}
        queryLabel="Search any vehicle details"
        onRowClick={(row: ILead) => { if (!claims?.isExporter) openInNewTab(`leads/${row.id}/parts`) }}
        actionButtons={[]}
        modeToggles={[
          { label: "Already Priced", pageMode: PageMode.ExportsPriced },
          { label: "Already Accepted", pageMode: PageMode.ExportsAccepted },
        ]}
        showTotal={true}
        additionalBody={additionalBody}
        // set the initial sort (SortingState) to scheduledCollectionOn
        // initialSort={[{ id: "scheduledCollectionOn", desc: true }]}
        expandedRow={(lead) => (
          <div className="space-y-4 p-4">

            <div className="bg-gray-100 w-[77vw]">
              <PartVehicleImages lead={lead!} showAll={!claims?.isExporter} collapsible={false} />
            </div>

            {!claims?.isExporter && (
              <div className="flex gap-x-10">
                <div className="flex flex-col">
                  <span className="font-bold">Vehicle Notes:</span>
                  <span>{lead.vehicle?.notes}</span>
                </div>

                <div className="flex flex-col">
                  <span className="font-bold">Office Notes:</span>
                  <span>{lead.officeNotes}</span>
                </div>

                <div className="flex flex-col">
                  <span className="font-bold">Driver Notes:</span>
                  <span>{lead.driverNotes}</span>
                </div>
              </div>
            )}

          </div>
        )}
      />

    </>
  );
}